import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaLock, FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";

import LoginService from "../../services/LoginService";
import AlertService from "../../services/AlertService";

import Fqicon from "../../assets/img/Fq.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { code } = useParams(); // 🔑 reset link code

  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [strengthPercent, setStrengthPercent] = useState(0);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [strengthColor, setStrengthColor] = useState("bg-red-500");

  /* ================= PASSWORD STRENGTH ================= */
  const handlePasswordChange = () => {
    const password = newPasswordRef.current.value;
    let score = 0;

    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[\W_]/.test(password)) score++;

    const percent = (score / 4) * 100;
    setStrengthPercent(percent);

    if (percent < 40) {
      setPasswordStrength("Weak");
      setStrengthColor("bg-red-500");
    } else if (percent < 80) {
      setPasswordStrength("Medium");
      setStrengthColor("bg-yellow-500");
    } else {
      setPasswordStrength("Strong");
      setStrengthColor("bg-green-600");
    }
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    let isValid = true;
    const newErrors = { newPassword: "", confirmPassword: "" };

    const newPassword = newPasswordRef.current.value;
    const confirmPassword = confirmPasswordRef.current.value;

    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

    if (!newPassword) {
      newErrors.newPassword = "Password is required";
      isValid = false;
    } else if (!strongPasswordRegex.test(newPassword)) {
      newErrors.newPassword =
        "Password must be 8+ chars, include uppercase, lowercase, number & symbol";
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        Key: code,
        Value: newPasswordRef.current.value,
      };

      const result = await LoginService.SetPassword(payload);

      if (result.isSuccess) {
        AlertService.success(
          "Your password has been changed successfully!"
        );
        navigate("/login");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("An error occurred during password reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">

      {/* LEFT PANEL */}
      <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
        <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
          <img src={Fqicon} alt="Logo" className="h-12" />
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
        <form onSubmit={handleSubmit} className="w-full max-w-[420px]">

          {/* TITLE */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <h2 className="text-2xl font-semibold">Reset Password</h2>
            <FaInfoCircle
              className="text-blue-600"
              title="Password must be strong"
            />
          </div>

          {/* NEW PASSWORD */}
          <div className="mb-4">
            <label className="text-base font-medium text-gray-700">
              New Password
            </label>

            <div className="flex items-center border rounded-md px-3 h-12 mt-1">
              <FaLock className="text-gray-500 mr-3" />

              <input
                ref={newPasswordRef}
                type={showPassword ? "text" : "password"}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                className="flex-1 outline-none text-base"
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {errors.newPassword && (
              <p className="text-red-500 text-sm mt-1">
                {errors.newPassword}
              </p>
            )}

            {/* STRENGTH BAR */}
            {strengthPercent > 0 && (
              <div className="mt-2">
                <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                  <div
                    className={`h-2 transition-all duration-300 ${strengthColor}`}
                    style={{ width: `${strengthPercent}%` }}
                  />
                </div>

                <p
                  className={`text-sm mt-1 font-medium ${
                    passwordStrength === "Weak"
                      ? "text-red-500"
                      : passwordStrength === "Medium"
                      ? "text-yellow-600"
                      : "text-green-600"
                  }`}
                >
                  {passwordStrength}
                </p>
              </div>
            )}
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="mb-6">
            <label className="text-base font-medium text-gray-700">
              Confirm Password
            </label>

            <div className="flex items-center border rounded-md px-3 h-12 mt-1">
              <FaLock className="text-gray-500 mr-3" />

              <input
                ref={confirmPasswordRef}
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                className="flex-1 outline-none text-base"
              />

              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="text-gray-500"
              >
                {showConfirm ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            className={`
              w-full h-12
              rounded-md
              text-white font-medium
              bg-[#0e3a46]
              hover:opacity-95
              ${loading && "opacity-50 cursor-not-allowed"}
            `}
          >
            {loading ? "Resetting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;